import {
  Box,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  InputLabel,
  Divider,
  Table,
  TableFooter,
} from '@mui/material';
import { useState, useMemo, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { handleGetFranchiseMKTReport } from '~/thunks/report/reportThunk';
import { handleApiError } from '~/utils/helper';
import { IProjectReport } from '~/utils/interface/report';
import { HeadCellType } from '~/utils/types/common';
import { useTranslation } from 'react-i18next';
import { BaseDatePicker, BaseHeaderTitle, BaseRow, BaseSelect } from '~/components/common';
import dayjs from 'dayjs';
import { handleGetAccountsSelection } from '~/thunks/account/accountThunk';

const FranchiseMKTReport = () => {
  const { t } = useTranslation('admin/report');
  const dispatch = useAppDispatch();

  const currentDate = new Date();
  const end = currentDate.toISOString().split('T')[0];
  const start = new Date(currentDate.setMonth(currentDate.getMonth() - 1)).toISOString().split('T')[0];
  const headCells: Array<HeadCellType> = useMemo(
    () => [
      { id: 'staffMember', label: t('properties.staffMember') },
      { id: 'totalActivities', label: t('properties.totalActivities') },
    ],
    [t]
  );

  const { selectionAccounts } = useAppSelector((state) => state.account);
  const [startDate, setStartDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState<string>(dayjs().format('YYYY-MM-DD'));

  const [selectedAccount, setSelectedAccount] = useState<Array<string>>([]); // State for selected account
  const [data, setData] = useState<Array<IProjectReport>>([]);
  const optionsWithAll = [{ id: 'all', name: 'All' }, ...selectionAccounts];

  const handleSearch = () => {
    if (selectedAccount.includes('all')) {
      dispatch(handleGetFranchiseMKTReport({ startDate, endDate }))
        .unwrap()
        .then((response) => {
          setData(response.result || []);
        })
        .catch(handleApiError);
    } else {
      dispatch(handleGetFranchiseMKTReport({ startDate, endDate, createdBy: selectedAccount ?? [] }))
        .unwrap()
        .then((response) => {
          setData(response.result || []);
        })
        .catch(handleApiError);
    }
  };

  useEffect(() => {
    dispatch(handleGetAccountsSelection({}))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    dispatch(handleGetFranchiseMKTReport({ startDate: start, endDate: end }))
      .unwrap()
      .then((response) => {
        setStartDate(start);
        setEndDate(end);

        setSelectedAccount(['all']);
        setData(response.result || []);
      })
      .catch(handleApiError);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <BaseHeaderTitle title={t('title.franchiseMKT')} />

      <Divider className='mb-[20px] mt-[20px]' />
      <BaseRow className='gap-[20px] '>
        <Box className='w-[900px]'>
          <InputLabel>{t('properties.startDate')}</InputLabel>
          <BaseDatePicker
            value={startDate}
            onChange={(value) => setStartDate(dayjs(value).format('YYYY-MM-DD'))}
            dateFormat='DD MMM, YYYY'
          />
        </Box>
        <Box className='w-[900px]'>
          <InputLabel>{t('properties.endDate')}</InputLabel>
          <BaseDatePicker
            value={endDate}
            onChange={(value) => setEndDate(dayjs(value).format('YYYY-MM-DD'))}
            dateFormat='DD MMM, YYYY'
          />
        </Box>
        <Box className='w-full'>
          <InputLabel>{t('properties.createdBy')}</InputLabel>
          <BaseSelect
            options={optionsWithAll}
            value={selectedAccount}
            onChange={(event) => {
              const selectedValue = event.target.value;

              if (selectedValue.includes('all') && !selectedAccount.includes('all')) {
                setSelectedAccount(['all']);
              } else if (selectedValue !== 'all' && selectedAccount.includes('all')) {
                setSelectedAccount(selectedValue.filter((value: any) => value !== 'all'));
              } else {
                setSelectedAccount(selectedValue);
              }
            }}
            label={t('properties.createdBy')}
            placeholder={t('properties.createdBy')}
            multiple
          />
        </Box>
        <Box className='w-[200px] mt-[12px]'>
          <InputLabel></InputLabel>
          <Button variant='contained' color='primary' onClick={handleSearch}>
            {t('search')}
          </Button>
        </Box>
      </BaseRow>

      <Divider className='mb-[20px] mt-[20px]' />
      <TableContainer className='max-h-[600px] overflow-auto'>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {headCells.map((cell) => (
                <TableCell key={cell.id} className='w-[50%] bg-[#F0F7FF] text-[#758CA5] text-lg font-bold'>
                  {cell.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => (
              <TableRow key={index}>
                <TableCell className='w-[50%]'>{row.name}</TableCell>
                <TableCell className='w-[50%]'>{row.totalActivity}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow className='sticky bottom-0 bg-[#F0F7FF]'>
              <TableCell className='w-[50%] font-bold text-[#758CA5]'>
                {data.filter((row) => row.name).length}
              </TableCell>

              <TableCell className='w-[50%] font-bold text-[#758CA5]'>
                {data.reduce((sum, row) => sum + (row.totalActivity || 0), 0)}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default FranchiseMKTReport;
